import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  useOutsideClick,
} from '@chakra-ui/react';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';

import { Badge } from 'components/atoms/badge';
import { FieldTitle } from 'components/atoms/form/fieldTitle';
import { UpDownOptionWithSelectValueProps } from 'components/atoms/form/inputList/upDownOptionWithSelect';
import {
  CareerSlugEnum,
  Focus,
  useGetActiveFocusesQuery,
  useGetFocusSuggestionsQuery,
} from 'lib/generated/graphql';
import { useJobFilters } from 'lib/repositories/jobsShowcaseFilters';

import FocusItems from './focusItem';

const MAX_WIDTH = 400;

interface Params {
  expands?: boolean;
  label?: string;
  subTitle?: string | React.ReactElement;
  placeholder?: string;
  selectedCareer?: string;
  onChange?: (focus: Focus) => void;
  ref?: ((instance: any) => void) | MutableRefObject<any> | null;
  isRequired?: boolean;
  isFull?: boolean;
  selectedItems?: UpDownOptionWithSelectValueProps[];
  onlyActiveJobs?: boolean;
}

export const AsyncSelectFocus = ({
  expands,
  label = 'Foco de Carreira',
  subTitle,
  selectedCareer,
  onChange,
  ref,
  isRequired,
  isFull,
  selectedItems,
  placeholder,
  onlyActiveJobs = false,
}: Params) => {
  const [pannelWidth, setPannelWidth] = useState(MAX_WIDTH);
  const [accordionIndex, setAccordionIndex] = useState<Array<number>>([]);
  const accordionRef = useRef<HTMLDivElement>(null);
  const { filters, setFocuses } = useJobFilters();
  const { data: allFocuses, loading: isLoadingFocuses } =
    useGetActiveFocusesQuery({
      fetchPolicy: 'cache-first',
      variables: {
        availableForCandidates: true,
        careerSlugs:
          (selectedCareer && ([selectedCareer] as CareerSlugEnum[])) ||
          undefined,
      },
    });

  const { data: suggestFocus, loading: isLoadingSuggestions } =
    useGetFocusSuggestionsQuery({
      fetchPolicy: 'cache-first',
      variables: {
        careerSlug: selectedCareer,
        onlyActiveJobs,
      },
    });

  useOutsideClick({
    ref: accordionRef,
    handler: () => {
      if (accordionIndex.length > 0) setAccordionIndex([]);
    },
  });

  useEffect(() => {
    const listener = () => {
      const w = window.document.documentElement.clientWidth;
      const percentage = 0.25;
      const maxW = MAX_WIDTH;
      setPannelWidth(w * percentage >= maxW ? maxW : w * percentage);
    };

    window.addEventListener('resize', listener);

    return () => window.removeEventListener('resize', listener);
  }, []);

  const handleUpdate = (focus: Focus) => {
    if (onChange) {
      onChange(focus);
    }
  };

  const data = onChange
    ? selectedItems?.map((item) => ({
        id: item.value,
        description: item.label,
        career: item.career,
      }))
    : filters.focuses;

  return (
    <Box w='full'>
      <FieldTitle
        mb={1}
        label={label}
        fontSize={14}
        fontWeight={700}
        color='text.900'
        ref={ref}
        isRequired={isRequired}
      />

      <Accordion
        index={accordionIndex}
        reduceMotion={!expands}
        width='full'
        py={1}
        color='text.300'
        allowMultiple
        border='none'
        boxShadow='none'
        ref={accordionRef}
        onChange={(e: number[]) => setAccordionIndex([...e])}
        dataTest='asyncSelectFocus'
      >
        <AccordionItem
          isDisabled={isLoadingFocuses || isLoadingSuggestions}
          border='none'
        >
          <AccordionButton
            data-testid='asyncSelectFocus-button'
            fontSize='sm'
            fontWeight={700}
            border='1px solid lightgray'
            borderRadius={2}
            _focus={{
              boxShadow: 'none',
              outline: 'none',
            }}
            _hover={{
              bgColor: 'background.50',
            }}
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            py={0}
            minH={10}
          >
            <Flex
              justify='start'
              wrap='wrap'
              w='93%'
              minH={9}
              borderRight='1px solid lightgrey'
            >
              {data && data.length > 0 ? (
                data.map((focus) => (
                  <Badge
                    mt={1}
                    mr={1}
                    mb={1}
                    text={focus.description}
                    key={focus.id}
                    isClosable
                    onClose={(e) => {
                      e.preventDefault();
                      if (onChange) {
                        onChange(focus as Focus);
                      }
                      setFocuses(
                        filters.focuses?.filter(({ id }) => id !== focus.id)
                      );
                    }}
                  />
                ))
              ) : (
                <Text color='text.200' fontWeight={600} mt={2}>
                  Selecione um ou mais focos
                </Text>
              )}
            </Flex>

            <Flex justify='center' alignItems='center' w={21}>
              <AccordionIcon fontSize='xl' color='primary.500' mr={-4} />
            </Flex>
          </AccordionButton>
          <AccordionPanel
            maxW={expands ? 'full' : pannelWidth}
            maxH={expands ? 500 : 350}
            w='100%'
            overflow='scroll'
            overflowX='hidden'
            mt={2}
            borderRadius={4}
            border='1px solid lightgray'
            position={expands ? 'relative' : 'absolute'}
            zIndex='popover'
            backgroundColor='white'
            pb={4}
            px={0}
          >
            {isFull && (
              <Flex
                alignItems='center'
                justifyContent='center'
                color='text.200'
              >
                Não é possível selecionar mais que 3 áreas{' '}
              </Flex>
            )}
            {!isFull && suggestFocus && allFocuses && (
              <FocusItems
                suggestFocus={
                  !onChange ||
                  suggestFocus?.getFocusSuggestions.focus.length > 10
                    ? suggestFocus?.getFocusSuggestions.focus
                    : undefined
                }
                allFocuses={allFocuses.getActiveFocuses}
                selectedItems={selectedItems}
                placeholder={placeholder}
                notifyChange={onChange && handleUpdate}
              />
            )}
          </AccordionPanel>
          {subTitle && (
            <Text color='text.200' fontSize='0.9rem' fontStyle='italic' mt={1}>
              {subTitle}
            </Text>
          )}
        </AccordionItem>
      </Accordion>
    </Box>
  );
};
